"use client"
import React, { useEffect, useState, useRef } from "react"
import { useNavigate, useParams } from "react-router-dom"

import PackageDetailsStep from "./steps/PackageDetailsStep"
import PackageContentStep from "./steps/PackageContentStep"
import PaymentOptionsStep, { PaymentOptionsStepRef } from "./steps/PaymentOptionsStep"
import FAQStep from "./steps/FAQStep"
import Modal from "../../components/common/Modal"
import { getPackageById, updatePackage } from "../../api/packageService"
import { ArrowLeft, Check } from "lucide-react"

const STORAGE_KEY = 'currentEditPackage'

const EditPackage: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const [currentStep, setCurrentStep] = useState(1)
  const [packageData, setPackageData] = useState({
    title: "",
    description: "",
    coverImage: null as File | null,
    coverUrl: null as string | null,
    bannerImage: null as File | null,
    bannerUrl: null as string | null,
    courses: [] as string[],
    telegramId: [] as string[],
    requirements: "",
    targetAudience: "",
    learningOutcomes: "",
    priceDetails: [] as any[],
    faqs: [] as Array<{question: string, answer: string}>
  })
  const [originalData, setOriginalData] = useState<any>({})
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage] = useState<string | null>(null)
  const [showSuccessModal, setShowSuccessModal] = useState(false)
  const [, setHasChanges] = useState(false)
  const [updatedFields, ] = useState<Record<string, boolean>>({})

  const formRefs = [
    useRef<HTMLFormElement>(null),
    useRef<HTMLFormElement>(null),
    useRef<HTMLFormElement>(null),
    useRef<HTMLFormElement>(null)
  ]
  
  // Add ref for payment step
  const paymentStepRef = useRef<PaymentOptionsStepRef>(null)

  const steps = [
    { id: 1, title: "Package details", completed: currentStep > 1, current: currentStep === 1 },
    { id: 2, title: "Package content", completed: currentStep > 2, current: currentStep === 2 },
    { id: 3, title: "Payment options", completed: currentStep > 3, current: currentStep === 3 },
    { id: 4, title: "FAQs", completed: currentStep > 4, current: currentStep === 4 },
  ]

  // Load existing package data
  useEffect(() => {
    const fetchPackageData = async () => {
      if (!id) return
      
      try {
        setLoading(true)
        const response = await getPackageById(id)
        
        if (response.status === 200 && response.data) {
          const data = response.data
          
          // Store original data for comparison
          const originalPriceDetails = data.priceDetails ? data.priceDetails.map((price: any) => ({
            period: price.period,
            actualPrice: parseFloat(price.actualPrice) || 0,
            price: parseFloat(price.price) || 0,
            gst: parseFloat(price.gst) || 0,
            totalprice: parseFloat(price.totalprice) || 0
          })) : [];
          
          setOriginalData({
            title: data.title || "",
            description: data.description || "",
            courses: data.courseIds || [],
            telegramId: data.telegramId || [],
            faqs: data.faqs ? data.faqs.map((faq: any) => ({
              question: faq.question,
              answer: faq.answer
            })) : [],
            priceDetails: originalPriceDetails
          })
          
// Process banner image
let bannerUrl: string | null = null

if (data.bannerBase64) {
  const rawBase64 = data.bannerBase64

  if (rawBase64.startsWith("data:image/")) {
    bannerUrl = rawBase64
  } else {
    bannerUrl = `data:image/jpeg;base64,${rawBase64}`
  }

  console.log("Using banner Base64 for edit preview")
} else if (data.bannerUrl) {
  bannerUrl = data.bannerUrl
  console.log("Using banner URL for edit preview:", bannerUrl)
}

// Process cover/theme image
let coverUrl: string | null = null

if (data.themeBase64) {
  const rawBase64 = data.themeBase64

  if (rawBase64.startsWith("data:image/")) {
    coverUrl = rawBase64
  } else {
    coverUrl = `data:image/jpeg;base64,${rawBase64}`
  }

  console.log("Using theme Base64 for edit preview")
} else if (data.themeUrl) {
  coverUrl = data.themeUrl
  console.log("Using theme URL for edit preview:", coverUrl)
}          
          
          const packageState = {
            title: data.title || "",
            description: data.description || "",
            coverImage: null,
            coverUrl: coverUrl,
            bannerImage: null,
            bannerUrl: bannerUrl,
            courses: data.courseIds || [],
            telegramId: data.telegramId || [],
            requirements: "",
            targetAudience: "",
            learningOutcomes: "",
            priceDetails: data.priceDetails ? data.priceDetails.map((price: any) => ({
              period: price.period,
              actualPrice: parseFloat(price.actualPrice) || 0,
              price: parseFloat(price.price) || 0,
              gst: parseFloat(price.gst) || 0,
              totalprice: parseFloat(price.totalprice) || 0
            })) : [],
            faqs: data.faqs ? data.faqs.map((faq: any) => ({
              question: faq.question,
              answer: faq.answer
            })) : []
          }
          
          setPackageData(packageState)
        } else {
          setError(response.message || "Failed to load package data")
        }
      } catch (err) {
        console.error("Error fetching package:", err)
        setError("Failed to load package data")
      } finally {
        setLoading(false)
      }
    }
    
    fetchPackageData()
  }, [id])

  // Save data to local storage when it changes
  useEffect(() => {
  if (Object.keys(packageData).length > 0) {
    // Don't save File objects OR large Base64 image previews to localStorage
    const {
      coverImage,
      bannerImage,
      coverUrl,
      bannerUrl,
      ...dataToSave
    } = packageData

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(dataToSave))
    } catch (error) {
      console.warn("Could not save edit package data to localStorage:", error)
    }

    setHasChanges(true)
  }
}, [packageData])

  // Clear local storage when component unmounts or when package is successfully updated
  useEffect(() => {
    return () => {
      // Only clear local storage if the package was not successfully updated
      if (!showSuccessModal && !successMessage) {
        localStorage.removeItem(STORAGE_KEY)
      }
    }
  }, [showSuccessModal, successMessage])

  // Clear local storage when page is refreshed
  useEffect(() => {
    const handleBeforeUnload = () => {
      // Only clear local storage if the package was not successfully updated
      if (!showSuccessModal && !successMessage) {
        localStorage.removeItem(STORAGE_KEY)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [showSuccessModal, successMessage])

  // const validateCurrentStep = () => {
  //   if (formRefs[currentStep - 1]?.current) {
  //     return formRefs[currentStep - 1].current!.checkValidity()
  //   }
  //   return true
  // }

  // Function to check if a field has changed
  const hasFieldChanged = (field: string, value: any): boolean => {
    if (!originalData.hasOwnProperty(field)) return true
    
    const originalValue = originalData[field]
    
    // Handle array comparison
    if (Array.isArray(value) && Array.isArray(originalValue)) {
      if (value.length !== originalValue.length) return true
      return !value.every((item, index) => item === originalValue[index])
    }
    
    // Handle FAQ comparison
    if (field === 'faqs' && Array.isArray(value) && Array.isArray(originalValue)) {
      if (value.length !== originalValue.length) return true
      
      return !value.every((faq, index) => {
        const originalFaq = originalValue[index]
        return faq.question === originalFaq.question && faq.answer === originalFaq.answer
      })
    }
    
    // Handle priceDetails comparison
    if (field === 'priceDetails' && Array.isArray(value) && Array.isArray(originalValue)) {
      if (value.length !== originalValue.length) return true
      
      return !value.every((price, index) => {
        const originalPrice = originalValue[index]
        const periodEqual = price.period === originalPrice.period
        const priceEqual = parseFloat(price.price) === parseFloat(originalPrice.price)
        const actualPriceEqual = parseFloat(price.actualPrice) === parseFloat(originalPrice.actualPrice)
        const gstEqual = parseFloat(price.gst) === parseFloat(originalPrice.gst)
        const totalpriceEqual = parseFloat(price.totalprice) === parseFloat(originalPrice.totalprice)
        
        return periodEqual && priceEqual && actualPriceEqual && gstEqual && totalpriceEqual
      })
    }
    
    // Handle simple value comparison
    return value !== originalValue
  }

  // Function to update package with only changed fields
  // const updatePackageStep = (stepData: any, stepNumber: number) => {
  //   // Just update the local state, don't hit the API
  //   setPackageData(stepData)
  //   return true
  // }

  const moveToNextStep = (updatedData?: any) => {
    // Use the updated data if provided, otherwise use the current packageData
    const dataToUpdate = updatedData || packageData
    // Update package data without hitting the API
    setPackageData(dataToUpdate)
    setCurrentStep(prev => prev + 1)
  }

  // const handleNext = async () => {
  //   // Special handling for payment step (step 3)
  //   if (currentStep === 3) {
  //     try {
  //       if (paymentStepRef.current) {
  //         await paymentStepRef.current.savePaymentDetails()
  //       }
  //       moveToNextStep()
  //     } catch (error: any) {
  //       setError(error.message || "Failed to save payment details")
  //     }
  //     return
  //   }
    
  //   if (validateCurrentStep()) {
  //     setCurrentStep(prev => prev + 1)
  //   } else {
  //     formRefs[currentStep - 1].current!.reportValidity()
  //   }
  // }

  const moveToPrevStep = () => {
    // Update package data without hitting the API
    setPackageData(packageData)
    setCurrentStep(prev => prev - 1)
  }

  // const handleBack = () => {
  //   if (currentStep > 1) {
  //     moveToPrevStep()
  //   }
  // }

  const handlePublish = async () => {
    try {
      setLoading(true)
      setError(null)
      
      // Validate all steps
      for (let i = 0; i < formRefs.length; i++) {
        if (formRefs[i]?.current && !formRefs[i].current!.checkValidity()) {
          setCurrentStep(i + 1)
          formRefs[i].current!.reportValidity()
          return
        }
      }
      
      // Additional validation for required fields
      if (!packageData.title || !packageData.description) {
        setError("Please complete all required fields before updating.")
        return
      }
      
      // Prepare update parameters based on the package data
      let updateParams: any = {
        packageId: id!
      }
      
      // Check each field for changes
      if (hasFieldChanged('title', packageData.title)) {
        updateParams.title = packageData.title
      }
      if (hasFieldChanged('description', packageData.description)) {
        updateParams.description = packageData.description
      }
      if (packageData.coverImage) updateParams.themeFile = packageData.coverImage
      if (packageData.bannerImage) updateParams.bannerFile = packageData.bannerImage
      if (hasFieldChanged('courses', packageData.courses)) {
        updateParams.courseIds = packageData.courses
      }
      if (hasFieldChanged('telegramId', packageData.telegramId)) {
        updateParams.telegramId = packageData.telegramId
      }
      if (hasFieldChanged('priceDetails', packageData.priceDetails)) {
        // Clean price details
        const cleanPriceDetails = packageData.priceDetails.map((price: any) => ({
          period: price.period,
          actualPrice: parseFloat(price.actualPrice) || 0,
          price: parseFloat(price.price) || 0,
          gst: parseFloat(price.gst) || 0,
          totalprice: parseFloat(price.totalprice) || 0
        }))
        updateParams.priceDetails = cleanPriceDetails
      }
      if (hasFieldChanged('faqs', packageData.faqs)) {
        // Clean FAQs
        const cleanFaqs = packageData.faqs.map((faq: any) => ({
          question: faq.question,
          answer: faq.answer
        }))
        updateParams.faqs = cleanFaqs
      }
      
      // Remove undefined values
      Object.keys(updateParams).forEach(key => {
        if (updateParams[key] === undefined) {
          delete updateParams[key]
        }
      })
      
      // Only make API call if there are fields to update
      if (Object.keys(updateParams).length > 1) { // packageId is always present
        const response = await updatePackage(updateParams)
        
        if (response.status !== 200) {
          throw new Error(response.message || "Failed to update package")
        }
        
        localStorage.removeItem(STORAGE_KEY)
        setError(null)
        
        // Show success modal
        setShowSuccessModal(true)
      } else {
        // No changes to update, but still show success
        localStorage.removeItem(STORAGE_KEY)
        setError(null)
        setShowSuccessModal(true)
      }
    } catch (error: any) {
      console.error("Error updating package:", error)
      setError(error.message || "Failed to update package. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const handleGoToDashboard = () => {
    setShowSuccessModal(false)
    navigate("/package", { replace: true })
  }

  const renderCurrentStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <PackageDetailsStep
            data={packageData}
            onChange={setPackageData}
            formRef={formRefs[0]}
            onNext={moveToNextStep}
          />
        )
      case 2:
        return (
          <PackageContentStep
            data={packageData}
            onChange={setPackageData}
            formRef={formRefs[1]}
            onNext={moveToNextStep}
            onBack={moveToPrevStep}
          />
        )
      case 3:
        return (
          <PaymentOptionsStep
            ref={paymentStepRef}
            data={{...packageData, priceDetails: packageData.priceDetails || []}}
            onChange={setPackageData}
            onNext={moveToNextStep}
            onBack={moveToPrevStep}
            isEditMode={true}
          />
        )
      case 4:
        return (
          <FAQStep
            data={{...packageData, faqs: packageData.faqs || []}}
            onChange={setPackageData}
            formRef={formRefs[3]}
            onBack={moveToPrevStep}
            onNext={handlePublish}
            loading={loading}
          />
        )
      default:
        return (
          <PackageDetailsStep
            data={packageData}
            onChange={setPackageData}
            formRef={formRefs[0]}
            onNext={moveToNextStep}
          />
        )
    }
  }

  if (loading && currentStep !== 4) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-500"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-3 sm:px-4 lg:px-6 py-3 shadow-sm">
        <div className="flex items-center">
          <button
            onClick={() => navigate("/package")}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors duration-200"
          >
            <ArrowLeft className="mr-1 sm:mr-2" size={16} />
            <span className="hidden sm:inline text-sm">Go to dashboard</span>
            <span className="sm:hidden text-sm">Back</span>
          </button>
        </div>
      </div>
      
      {/* Progress Bar */}
      <div className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6 py-3">
          <div className="flex justify-between items-center mb-2">
            <h2 className="text-xs sm:text-sm font-semibold text-gray-900">Progress</h2>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {Math.round((currentStep / steps.length) * 100)}% Complete
            </span>
          </div>
          <div className="relative">
            <div className="flex justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex flex-col items-center relative z-10">
                  <div
                    className={`w-6 h-6 sm:w-8 sm:h-8 rounded-full flex items-center justify-center text-xs font-semibold transition-all duration-300 ${
                      step.completed || updatedFields[step.id]
                        ? "bg-green-500 text-white shadow-md"
                        : step.current
                        ? "bg-blue-600 text-white shadow-md ring-2 ring-blue-200"
                        : "bg-gray-200 text-gray-600"
                    }`}
                  >
                    {step.completed || updatedFields[step.id] ? (
                      <Check className="w-3 h-3 sm:w-4 sm:h-4" />
                    ) : (
                      <span>{step.id}</span>
                    )}
                  </div>
                  <span className={`mt-1 sm:mt-2 text-xs font-medium text-center max-w-12 sm:max-w-16 lg:max-w-none leading-tight ${
                    step.current ? "text-blue-600" : "text-gray-500"
                  }`}>
                    {step.title}
                  </span>
                </div>
              ))}
            </div>
            <div className="absolute top-3 sm:top-4 left-3 sm:left-4 right-3 sm:right-4 h-0.5 bg-gray-200 -z-0">
              <div
                className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500 ease-out"
                style={{ width: `${((currentStep - 1) / (steps.length - 1)) * 100}%` }}
              />
            </div>
          </div>
        </div>
      </div>      
      
      {/* Main Content */}
      <div className="max-w-4xl mx-auto p-3 sm:p-4 lg:p-6">
        <div className="bg-white rounded-lg sm:rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-3 sm:p-4 lg:p-8">
            {/* Error Display */}
            {error && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4 flex items-start space-x-3">
                <div className="bg-red-100 rounded-full p-1 flex-shrink-0">
                  <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-red-800">Error occurred</h4>
                  <p className="text-sm text-red-700 mt-1">{error}</p>
                </div>
              </div>
            )}
            
            {/* Success Display */}
            {successMessage && (
              <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4 flex items-start space-x-3">
                <div className="bg-green-100 rounded-full p-1 flex-shrink-0">
                  <Check className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-green-800">Success!</h4>
                  <p className="text-sm text-green-700 mt-1">{successMessage}</p>
                </div>
              </div>
            )}
            
            {/* Step Content */}
            {renderCurrentStep()}
          </div>
        </div>
      </div>
      
      {/* Success Modal */}
      <Modal
        isOpen={showSuccessModal}
        onClose={() => {}}
        className="w-full max-w-md"
      >
        <div className="text-center p-6">
          <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-gradient-to-r from-green-100 to-emerald-100 mb-4">
            <Check className="h-8 w-8 text-green-600" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 mb-2">Package Updated Successfully!</h3>
          <p className="text-gray-600 mb-6">
            Your package "{packageData.title}" has been updated and is now available for students.
          </p>
          <button
            onClick={handleGoToDashboard}
            className="w-full px-6 py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-xl hover:from-green-700 hover:to-emerald-700 transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl font-semibold flex items-center justify-center space-x-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
            <span>Go to Dashboard</span>
          </button>
        </div>
      </Modal>
    </div>
  )
}

export default EditPackage